import type { Espace, Imputation } from '@mairistem/comptabilite';
import { isEmpty, isNil } from 'lodash';
import type ResultatTransfertComptabilite from '../../interfaces/simulation/resultatTransfertComptabilite';
import type { LigneEspace } from '../../services/simulation/elementComptabilite';
import { getLibelleCodesImputation } from '../imputation';

const toIdentifiants = (value: unknown): number[] =>
	Array.isArray(value)
		? value.filter((v): v is number => typeof v === 'number')
		: [];

/**
 * Lit le "resultat" d'un job de transfert en comptabilite termine avec succes.
 * Retourne null si le resultat est vide (ancien job) ou n'est pas un JSON exploitable.
 */
export const parseResultatTransfert = (
	resultat?: string | null,
): ResultatTransfertComptabilite | null => {
	if (isEmpty(resultat?.trim())) return null;

	try {
		const parsed = JSON.parse(resultat);
		if (typeof parsed !== 'object' || parsed === null) return null;

		return {
			espaces: toIdentifiants(parsed.espaces),
			imputations: toIdentifiants(parsed.imputations),
			lignesEspace: toIdentifiants(parsed.lignesEspace),
		};
	} catch {
		return null;
	}
};

const ELEMENTS_CREES: {
	feminin: boolean;
	key: keyof ResultatTransfertComptabilite;
	pluriel: string;
	singulier: string;
}[] = [
	{
		feminin: true,
		key: 'imputations',
		pluriel: 'imputations',
		singulier: 'imputation',
	},
	{ feminin: false, key: 'espaces', pluriel: 'espaces', singulier: 'espace' },
	{
		feminin: true,
		key: 'lignesEspace',
		pluriel: "lignes d'espace",
		singulier: "ligne d'espace",
	},
];

// "a, b et c"
const joinListe = (elements: string[]) =>
	elements.length > 1
		? `${elements.slice(0, -1).join(', ')} et ${elements.at(-1)}`
		: elements.join('');

export const hasCreations = (
	resultat: ResultatTransfertComptabilite | null,
): boolean => ELEMENTS_CREES.some(({ key }) => !isEmpty(resultat?.[key]));

/**
 * Resume d'un transfert ayant cree des elements, par exemple : "2 imputations et
 * 1 ligne d'espace utilisées en 2026 et inexistantes en 2027 ont été créées sur l'exercice 2027."
 * @param anneeExercice Annee de l'exercice de la simulation (N)
 */
export const getResumeTransfert = (
	resultat: ResultatTransfertComptabilite,
	anneeExercice?: number,
): string => {
	const hasAnnee = !isNil(anneeExercice);
	const exercice = hasAnnee
		? `l'exercice ${anneeExercice + 1}`
		: "l'exercice suivant";

	const crees = ELEMENTS_CREES.filter(({ key }) => !isEmpty(resultat[key]));

	const total = crees.reduce((sum, { key }) => sum + resultat[key].length, 0);
	const pluriel = total > 1;
	// Le masculin l'emporte des qu'un espace a ete cree
	const feminin = crees.every((element) => element.feminin);
	const accorder = (participe: string) =>
		`${participe}${feminin ? 'e' : ''}${pluriel ? 's' : ''}`;

	const liste = joinListe(
		crees.map(({ key, pluriel: libellePluriel, singulier }) => {
			const count = resultat[key].length;
			return `${count} ${count > 1 ? libellePluriel : singulier}`;
		}),
	);
	const utilisation = hasAnnee
		? ` ${accorder('utilisé')} en ${anneeExercice} et ${accorder('inexistant')} en ${anneeExercice + 1}`
		: '';

	return `Transfert effectué.\n${liste}${utilisation} ${pluriel ? 'ont' : 'a'} été ${accorder('créé')} sur ${exercice}.`;
};

export type LibellesRapport = Record<
	keyof ResultatTransfertComptabilite,
	Record<number, string>
>;

export interface ElementsRapport {
	espaces: Map<number, Espace>;
	imputations: Map<number, Imputation>;
	lignesEspace: Map<number, LigneEspace>;
}

export const getLibelleIntrouvable = (identifiant: number) =>
	`n°${identifiant} introuvable`;

const formatLibelleImputation = (imputation?: Imputation) =>
	imputation
		? getLibelleCodesImputation(
				imputation.sens ?? '',
				imputation.chapitre?.section ?? '',
				imputation.chapitre?.code ?? '',
				imputation.article?.code ?? '',
				imputation.operation?.code,
				imputation.fonction?.code,
				imputation.ventilation?.code,
				imputation.analytique?.code,
			)
		: '';

const formatCodeLibelle = (element?: { code?: string; libelle?: string }) =>
	[element?.code, element?.libelle].filter(Boolean).join(' - ');

const formatLibelleLigneEspace = (ligne: LigneEspace) => {
	const imputation =
		formatLibelleImputation(ligne.imputation) ||
		`Imputation n°${ligne.identifiantImputation}`;
	// Le service est porte par l'espace de la ligne, a defaut l'espace lui-meme
	const service =
		formatCodeLibelle(ligne.espace?.service) ||
		formatCodeLibelle(ligne.espace) ||
		`Espace n°${ligne.identifiantEspace}`;

	return `${imputation} / ${service}`;
};

/**
 * Libelle de chaque element cree, a partir des elements recuperes par lot.
 * Un element non recupere a un libelle de repli.
 */
export const getLibellesRapport = (
	resultat: ResultatTransfertComptabilite,
	elements: ElementsRapport,
): LibellesRapport => {
	const toLibelles = <T>(
		ids: number[],
		map: Map<number, T>,
		format: (element: T) => string,
	) =>
		Object.fromEntries(
			ids.map((identifiant) => {
				const element = map.get(identifiant);
				const libelle = element ? format(element) : '';
				return [identifiant, libelle || getLibelleIntrouvable(identifiant)];
			}),
		);

	return {
		espaces: toLibelles(resultat.espaces, elements.espaces, formatCodeLibelle),
		imputations: toLibelles(
			resultat.imputations,
			elements.imputations,
			formatLibelleImputation,
		),
		lignesEspace: toLibelles(
			resultat.lignesEspace,
			elements.lignesEspace,
			formatLibelleLigneEspace,
		),
	};
};
